<template>
  <section class="legal-workspace">
    <nav class="legal-document-tabs" aria-label="Legal documents">
      <button
        v-for="document in documents"
        :key="document.documentId"
        type="button"
        class="legal-document-tab"
        :class="{ active: activeDocumentId === document.documentId }"
        @click="selectDocument(document.documentId)"
      >
        {{ document.title }}
        <span
          v-if="document.draft"
          class="tab-draft-indicator"
          aria-label="Saved draft"
          title="Saved draft"
        />
      </button>
    </nav>

    <details class="markdown-guide">
      <summary>
        <span class="markdown-guide-title">
          <span class="material-icons-outlined">menu_book</span>
          Markdown Formatting Guide
        </span>

        <span class="markdown-guide-toggle">
          Show guide
          <span class="material-icons-outlined guide-chevron">expand_more</span>
        </span>
      </summary>

      <div class="markdown-guide-content">
        <p class="markdown-guide-intro">
          Add these symbols in Edit mode. Leave a blank line between headings,
          paragraphs, and lists for consistent display across the website and apps.
        </p>

        <div class="markdown-guide-grid">
          <div class="markdown-guide-item">
            <code># Document Title</code>
            <span>Largest heading</span>
          </div>

          <div class="markdown-guide-item">
            <code>## Main Section</code>
            <span>Second-level heading</span>
          </div>

          <div class="markdown-guide-item">
            <code>### Subsection</code>
            <span>Third-level heading</span>
          </div>

          <div class="markdown-guide-item">
            <code>**Bold text**</code>
            <span>Important or emphasized language</span>
          </div>

          <div class="markdown-guide-item">
            <code>*Italic text*</code>
            <span>Italic emphasis</span>
          </div>

          <div class="markdown-guide-item">
            <code>- Bullet item</code>
            <span>Unordered list item</span>
          </div>

          <div class="markdown-guide-item">
            <code>1. Numbered item</code>
            <span>Ordered list item</span>
          </div>

          <div class="markdown-guide-item">
            <code>[Link text](https://example.com)</code>
            <span>Clickable external link</span>
          </div>

          <div
            v-if="activeDocumentId === 'limited-scope-agreement'"
            class="markdown-guide-item"
          >
            <code v-pre>{{CLIENT_NAME}}</code>
            <span>Required client-name placeholder for the signed contract</span>
          </div>

          <div
            v-if="activeDocumentId === 'limited-scope-agreement'"
            class="markdown-guide-item"
          >
            <code v-pre>{{SIGNED_DATE}}</code>
            <span>Required signing-date placeholder for the executed contract</span>
          </div>
        </div>
      </div>
    </details>

    <div v-if="loading" class="legal-status-card">
      <span class="material-icons-outlined">hourglass_top</span>
      <p>Loading legal documents...</p>
    </div>

    <div v-else-if="activeDocument" class="legal-document-card">
      <header class="legal-document-header">
        <div>
          <div class="legal-document-kicker">Shared legal source</div>
          <h3>{{ activeDocument.title }}</h3>
          <p>
            {{ activeDocument.description }}
          </p>

          <a
            v-if="activeDocument.publicURL"
            class="legal-public-link"
            :href="activeDocument.publicURL"
            target="_blank"
            rel="noopener noreferrer"
          >
            View public page
            <span class="material-icons-outlined">open_in_new</span>
          </a>
        </div>

        <button
          v-if="!editing"
          type="button"
          class="edit-mode-button"
          @click="beginEditing"
        >
          <span class="material-icons-outlined">edit</span>
          Edit
        </button>
      </header>

      <div v-if="activeDocument.draft && !editing" class="saved-draft-notice">
        <span class="material-icons-outlined">edit_note</span>
        <div>
          <strong>Saved draft available</strong>
          <span>
            Last saved {{ formatDateTime(activeDocument.draft.updatedAt) }}.
            The published version is still unchanged.
          </span>
        </div>
      </div>

      <div class="legal-document-metadata">
        <div>
          <span>Current version</span>
          <strong>{{ activeDocument.versionId || "Not published" }}</strong>
        </div>

        <div>
          <span>Effective</span>
          <strong>{{ formatDate(activeDocument.effectiveAt) }}</strong>
        </div>

        <div>
          <span>{{ activeDocument.reviewMode === "reference" ? "Usage" : "App review" }}</span>
          <strong>{{ reviewModeLabel(activeDocument.reviewMode) }}</strong>
        </div>

        <div>
          <span>Format</span>
          <strong>Markdown</strong>
        </div>
      </div>

      <div v-if="errorMessage" class="legal-message error" role="alert">
        {{ errorMessage }}
      </div>

      <div v-if="successMessage" class="legal-message success" role="status">
        {{ successMessage }}
      </div>

      <div v-if="editing" class="legal-editor">
        <div class="legal-editor-heading">
          <div>
            <h4>Edit {{ activeDocument.title }}</h4>
            <p>
              Markdown headings and lists are preserved for the website, iOS, and Android readers.
            </p>
          </div>

          <span class="draft-badge">
            {{ activeDocument.draft ? "Saved draft" : "Unpublished draft" }}
          </span>
        </div>

        <label for="legal-content-editor">Document language</label>
        <textarea
          id="legal-content-editor"
          v-model="draftContent"
          rows="24"
          spellcheck="true"
        />

        <div class="legal-editor-actions">
          <button
            type="button"
            class="secondary-button"
            :disabled="savingAction"
            @click="exitEditor"
          >
            Exit Editor
          </button>

          <button
            type="button"
            class="draft-button"
            :disabled="savingAction || !draftContent.trim()"
            @click="saveDraft"
          >
            {{ savingAction === "draft" ? "Saving Draft..." : "Save Draft" }}
          </button>

          <button
            type="button"
            class="publish-button"
            :disabled="savingAction || !draftContent.trim()"
            @click="saveNewVersion"
          >
            {{ savingAction === "publish" ? "Publishing..." : "Publish New Version" }}
          </button>
        </div>
      </div>

      <article v-else class="legal-document-preview">
        <template v-for="(block, index) in formattedBlocks" :key="`${block.type}-${index}`">
          <h1 v-if="block.type === 'heading-1'">
            <InlineMarkdown :text="block.text" />
          </h1>
          <h2 v-else-if="block.type === 'heading-2'">
            <InlineMarkdown :text="block.text" />
          </h2>
          <h3 v-else-if="block.type === 'heading-3'">
            <InlineMarkdown :text="block.text" />
          </h3>

          <ul v-else-if="block.type === 'list'">
            <li v-for="item in block.items" :key="item">
              <InlineMarkdown :text="item" />
            </li>
          </ul>

          <ol v-else-if="block.type === 'ordered-list'">
            <li v-for="item in block.items" :key="item">
              <InlineMarkdown :text="item" />
            </li>
          </ol>

          <p v-else><InlineMarkdown :text="block.text" /></p>
        </template>
      </article>
    </div>
  </section>
</template>

<script>
import { computed, h, onMounted, ref } from "vue";
import {
  fetchLegalDocumentDrafts,
  fetchLegalDocuments,
  publishLegalDocumentVersion,
  saveLegalDocumentDraft
} from "@/firebase/firestore";

const placeholderContent = `# Lorem Ipsum

## Purpose

Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.

## Terms

Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.

- Lorem ipsum dolor sit amet.
- Consectetur adipiscing elit.
- Sed do eiusmod tempor incididunt.

## Additional Information

Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur.`;

const documentDefinitions = [
  {
    documentId: "limited-scope-agreement",
    title: "Limited Scope Agreement",
    description: "The general limited representation agreement signed by App Clients.",
    reviewMode: "signature",
    publicURL: "https://legal.askmaix.com/app-contract"
  },
  {
    documentId: "privacy-policy",
    title: "Privacy Policy",
    description: "The privacy policy shared by the apps and public website.",
    reviewMode: "acknowledgment",
    publicURL: "https://legal.askmaix.com/privacy"
  },
  {
    documentId: "app-terms-conditions",
    title: "App Terms & Conditions",
    description: "The terms governing access to and use of the Maix application.",
    reviewMode: "acknowledgment",
    publicURL: "https://legal.askmaix.com/terms"
  },
  {
    documentId: "ai-confidentiality",
    title: "AI & Confidentiality",
    description: "The explanation of Maix AI use and confidentiality safeguards.",
    reviewMode: "acknowledgment",
    publicURL: "https://legal.askmaix.com/ai-confidentiality"
  },
  {
    documentId: "example-app-contract",
    title: "Example App Contract",
    description: "The public example of the App Client limited-scope contract for the website.",
    reviewMode: "reference",
    publicURL: "https://legal.askmaix.com/app-contract"
  }
];

const InlineMarkdown = {
  name: "InlineMarkdown",

  props: {
    text: {
      type: String,
      default: ""
    }
  },

  setup(props) {
    return () => h(
      "span",
      parseInlineMarkdown(props.text).map((segment, index) => {
        const key = `${segment.type}-${index}`;

        if (segment.type === "strong") {
          return h("strong", { key }, segment.text);
        }

        if (segment.type === "emphasis") {
          return h("em", { key }, segment.text);
        }

        if (segment.type === "link") {
          return h("a", {
            key,
            class: "inline-markdown-link",
            href: segment.url,
            target: "_blank",
            rel: "noopener noreferrer"
          }, segment.text);
        }

        return segment.text;
      })
    );
  }
};

export default {
  name: "LegalTermsDashboard",

  components: {
    InlineMarkdown
  },

  props: {
    adminId: {
      type: String,
      required: true
    },
    adminName: {
      type: String,
      default: ""
    }
  },

  setup(props) {
    const documents = ref(documentDefinitions.map(document => ({
      ...document,
      content: placeholderContent,
      contentFormat: "markdown",
      versionId: "",
      effectiveAt: null
    })));
    const activeDocumentId = ref(documentDefinitions[0].documentId);
    const loading = ref(true);
    const editing = ref(false);
    const savingAction = ref("");
    const draftContent = ref("");
    const editingBaseline = ref("");
    const errorMessage = ref("");
    const successMessage = ref("");

    const activeDocument = computed(() => {
      return documents.value.find(document => {
        return document.documentId === activeDocumentId.value;
      }) ?? null;
    });

    const formattedBlocks = computed(() => {
      const visibleContent = activeDocument.value?.draft?.content
        ?? activeDocument.value?.content
        ?? "";
      return parseMarkdownBlocks(visibleContent);
    });

    const loadDocuments = async () => {
      loading.value = true;
      errorMessage.value = "";

      try {
        const savedDocuments = await fetchLegalDocuments();
        let savedDrafts = [];

        try {
          savedDrafts = await fetchLegalDocumentDrafts();
        } catch (error) {
          console.warn("Saved legal drafts are unavailable:", error);
          errorMessage.value =
            "Published documents loaded, but saved drafts are unavailable until the updated Firestore rules are deployed.";
        }
        const savedById = new Map(savedDocuments.map(document => [
          document.documentId,
          document
        ]));
        const draftsById = new Map(savedDrafts.map(draft => [
          draft.documentId,
          draft
        ]));

        documents.value = documentDefinitions.map(definition => {
          const savedDocument = savedById.get(definition.documentId);
          let initialContent = placeholderContent;

          if (definition.documentId === "example-app-contract" && !savedDocument) {
            const agreement = savedById.get("limited-scope-agreement")?.content;
            initialContent = agreement
              ? agreement
                  .replaceAll("{{CLIENT_NAME}}", "Example App Client")
                  .replaceAll("{{SIGNED_DATE}}", "Date electronically signed by Client")
              : placeholderContent;
          }

          return {
            ...definition,
            content: initialContent,
            contentFormat: "markdown",
            versionId: "",
            effectiveAt: null,
            draft: draftsById.get(definition.documentId) ?? null,
            ...savedDocument
          };
        });
      } catch (error) {
        console.error("❌ Unable to load legal documents:", error);
        errorMessage.value = "Unable to load saved legal documents. Placeholder content is shown.";
      } finally {
        loading.value = false;
      }
    };

    const selectDocument = documentId => {
      if (editing.value && draftContent.value !== editingBaseline.value) {
        const discardDraft = window.confirm(
          "Discard the unsaved changes and open another legal document?"
        );

        if (!discardDraft) { return; }
      }

      activeDocumentId.value = documentId;
      editing.value = false;
      draftContent.value = "";
      editingBaseline.value = "";
      errorMessage.value = "";
      successMessage.value = "";
    };

    const beginEditing = () => {
      const editableContent = activeDocument.value?.draft?.content
        ?? activeDocument.value?.content
        ?? "";
      draftContent.value = editableContent;
      editingBaseline.value = editableContent;
      errorMessage.value = "";
      successMessage.value = "";
      editing.value = true;
    };

    const exitEditor = () => {
      if (draftContent.value !== editingBaseline.value) {
        const discardChanges = window.confirm(
          "Exit the editor and discard your unsaved changes? Any previously saved draft will remain available."
        );

        if (!discardChanges) { return; }
      }

      draftContent.value = "";
      editingBaseline.value = "";
      errorMessage.value = "";
      editing.value = false;
    };

    const saveDraft = async () => {
      const document = activeDocument.value;
      const normalizedContent = draftContent.value.trim();

      if (!document || !normalizedContent || savingAction.value) { return; }

      savingAction.value = "draft";
      errorMessage.value = "";
      successMessage.value = "";

      try {
        await saveLegalDocumentDraft({
          documentId: document.documentId,
          title: document.title,
          content: normalizedContent,
          reviewMode: document.reviewMode,
          basedOnVersionId: document.versionId ?? "",
          adminId: props.adminId,
          adminName: props.adminName
        });

        await loadDocuments();
        draftContent.value = "";
        editingBaseline.value = "";
        editing.value = false;
        successMessage.value = "Draft saved. The published version is unchanged.";
      } catch (error) {
        console.error("❌ Unable to save legal document draft:", error);
        errorMessage.value = error?.message ?? "Unable to save the legal document draft.";
      } finally {
        savingAction.value = "";
      }
    };

    const saveNewVersion = async () => {
      const document = activeDocument.value;
      const normalizedContent = draftContent.value.trim();

      if (!document || !normalizedContent || savingAction.value) { return; }

      if (
        document.documentId === "limited-scope-agreement"
        && !normalizedContent.includes("{{CLIENT_NAME}}")
      ) {
        errorMessage.value = "The Limited Scope Agreement must include {{CLIENT_NAME}} before publishing.";
        return;
      }

      if (
        document.documentId === "limited-scope-agreement"
        && !normalizedContent.includes("{{SIGNED_DATE}}")
      ) {
        errorMessage.value = "The Limited Scope Agreement must include {{SIGNED_DATE}} before publishing.";
        return;
      }

      const confirmationMessage = document.reviewMode === "reference"
        ? "Save this as the new current website example? The prior version remains in history."
        : "Save this as the new current version? This creates a new effective date for app review and preserves the prior version.";
      const confirmed = window.confirm(confirmationMessage);

      if (!confirmed) { return; }

      savingAction.value = "publish";
      errorMessage.value = "";
      successMessage.value = "";

      try {
        await publishLegalDocumentVersion({
          documentId: document.documentId,
          title: document.title,
          content: normalizedContent,
          reviewMode: document.reviewMode,
          clearDraft: Boolean(document.draft),
          adminId: props.adminId,
          adminName: props.adminName
        });

        await loadDocuments();
        editing.value = false;
        draftContent.value = "";
        editingBaseline.value = "";
        successMessage.value = "The new version is current and the prior version remains in history.";
      } catch (error) {
        console.error("❌ Unable to save legal document:", error);
        errorMessage.value = error?.message ?? "Unable to save the new legal document version.";
      } finally {
        savingAction.value = "";
      }
    };

    const formatDate = value => {
      const date = value?.toDate?.() ?? (value instanceof Date ? value : null);

      if (!date) { return "Not published"; }

      return new Intl.DateTimeFormat("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric"
      }).format(date);
    };

    const formatDateTime = value => {
      const date = value?.toDate?.() ?? (value instanceof Date ? value : null);

      if (!date) { return "recently"; }

      return new Intl.DateTimeFormat("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit"
      }).format(date);
    };

    const reviewModeLabel = reviewMode => {
      if (reviewMode === "signature") { return "Signature required"; }
      if (reviewMode === "reference") { return "Website reference"; }
      return "Acknowledgment required";
    };

    onMounted(loadDocuments);

    return {
      documents,
      activeDocumentId,
      activeDocument,
      formattedBlocks,
      loading,
      editing,
      savingAction,
      draftContent,
      errorMessage,
      successMessage,
      selectDocument,
      beginEditing,
      exitEditor,
      saveDraft,
      saveNewVersion,
      formatDate,
      formatDateTime,
      reviewModeLabel
    };
  }
};

function parseMarkdownBlocks(markdown) {
  const lines = String(markdown).split(/\r?\n/);
  const blocks = [];
  let paragraph = [];
  let listItems = [];
  let listType = null;

  const flushParagraph = () => {
    if (!paragraph.length) { return; }
    blocks.push({ type: "paragraph", text: paragraph.join(" ") });
    paragraph = [];
  };

  const flushList = () => {
    if (!listItems.length) { return; }
    blocks.push({
      type: listType === "ordered" ? "ordered-list" : "list",
      items: listItems
    });
    listItems = [];
    listType = null;
  };

  lines.forEach(line => {
    const trimmed = line.trim();
    const heading = trimmed.match(/^(#{1,3})\s+(.+)$/);
    const unorderedListItem = trimmed.match(/^[-*]\s+(.+)$/);
    const orderedListItem = trimmed.match(/^\d+[.]\s+(.+)$/);

    if (heading) {
      flushParagraph();
      flushList();
      blocks.push({
        type: `heading-${heading[1].length}`,
        text: heading[2]
      });
      return;
    }

    if (unorderedListItem || orderedListItem) {
      flushParagraph();
      const nextListType = orderedListItem ? "ordered" : "unordered";

      if (listType && listType !== nextListType) {
        flushList();
      }

      listType = nextListType;
      listItems.push((orderedListItem ?? unorderedListItem)[1]);
      return;
    }

    if (!trimmed) {
      flushParagraph();
      flushList();
      return;
    }

    flushList();
    paragraph.push(trimmed);
  });

  flushParagraph();
  flushList();
  return blocks;
}

function parseInlineMarkdown(value) {
  const source = String(value);
  const tokenPattern = /(\[[^\]]+\]\(https?:\/\/[^\s)]+\)|\*\*[^*]+\*\*|\*[^*]+\*)/g;
  const segments = [];
  let cursor = 0;

  for (const match of source.matchAll(tokenPattern)) {
    if (match.index > cursor) {
      segments.push({ type: "text", text: source.slice(cursor, match.index) });
    }

    const token = match[0];
    const link = token.match(/^\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)$/);

    if (link) {
      segments.push({ type: "link", text: link[1], url: link[2] });
    } else if (token.startsWith("**")) {
      segments.push({ type: "strong", text: token.slice(2, -2) });
    } else {
      segments.push({ type: "emphasis", text: token.slice(1, -1) });
    }

    cursor = match.index + token.length;
  }

  if (cursor < source.length) {
    segments.push({ type: "text", text: source.slice(cursor) });
  }

  return segments;
}
</script>

<style scoped>
.legal-workspace {
  display: flex;
  flex-direction: column;
  min-height: 100%;
  gap: 18px;
}

.legal-document-tabs {
  display: flex;
  align-items: center;
  gap: 0;
  padding: 9px;
  overflow-x: auto;
  border-radius: 14px;
  background: #082d49;
}

.legal-document-tab {
  position: relative;
  flex: 1 0 auto;
  padding: 14px 20px;
  border: 0;
  border-right: 1px solid rgba(255, 255, 255, 0.32);
  background: transparent;
  color: #f5b800;
  font: inherit;
  font-weight: 700;
  cursor: pointer;
}

.legal-document-tab:last-child {
  border-right: 0;
}

.legal-document-tab::after {
  position: absolute;
  right: 20px;
  bottom: 7px;
  left: 20px;
  height: 2px;
  border-radius: 2px;
  background: transparent;
  content: "";
}

.legal-document-tab.active {
  color: #ffffff;
}

.legal-document-tab.active::after {
  background: #f5b800;
}

.tab-draft-indicator {
  display: inline-block;
  width: 7px;
  height: 7px;
  margin-left: 7px;
  border-radius: 50%;
  background: #ffffff;
  box-shadow: 0 0 0 2px rgba(245, 184, 0, 0.65);
  vertical-align: middle;
}

.markdown-guide {
  flex-shrink: 0;
  overflow: hidden;
  border: 1px solid #d9e4df;
  border-radius: 14px;
  background: #ffffff;
  box-shadow: 0 5px 18px rgba(20, 40, 30, 0.04);
}

.markdown-guide summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding: 16px 20px;
  color: #173047;
  cursor: pointer;
  list-style: none;
}

.markdown-guide summary::-webkit-details-marker {
  display: none;
}

.markdown-guide-title,
.markdown-guide-toggle {
  display: inline-flex;
  align-items: center;
}

.markdown-guide-title {
  gap: 10px;
  font-weight: 800;
}

.markdown-guide-title .material-icons-outlined {
  color: #16945d;
  font-size: 20px;
}

.markdown-guide-toggle {
  gap: 5px;
  color: #64726b;
  font-size: 0.82rem;
  font-weight: 700;
}

.guide-chevron {
  font-size: 20px;
  transition: transform 0.18s ease;
}

.markdown-guide[open] .guide-chevron {
  transform: rotate(180deg);
}

.markdown-guide[open] .markdown-guide-toggle {
  font-size: 0;
}

.markdown-guide[open] .markdown-guide-toggle::before {
  font-size: 0.82rem;
  content: "Hide guide";
}

.markdown-guide-content {
  padding: 0 20px 20px;
  border-top: 1px solid #e7ece9;
}

.markdown-guide-intro {
  margin: 16px 0;
  color: #65716b;
  line-height: 1.55;
}

.markdown-guide-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.markdown-guide-item {
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
  padding: 12px 14px;
  border: 1px solid #e1e7e4;
  border-radius: 9px;
  background: #f8faf9;
}

.markdown-guide-item code {
  flex: 0 1 48%;
  overflow-wrap: anywhere;
  color: #0b5f3c;
  font-size: 0.82rem;
  font-weight: 700;
}

.markdown-guide-item span {
  color: #6b7771;
  font-size: 0.82rem;
}

.legal-status-card,
.legal-document-card {
  border: 1px solid #dde4e0;
  border-radius: 16px;
  background: #ffffff;
  box-shadow: 0 8px 24px rgba(20, 40, 30, 0.05);
}

.legal-status-card {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 280px;
  gap: 12px;
  color: #6c7772;
}

.legal-document-card {
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.legal-document-header {
  display: flex;
  flex-shrink: 0;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
  padding: 28px 30px 22px;
}

.legal-document-kicker {
  margin-bottom: 6px;
  color: #159c62;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.legal-document-header h3 {
  margin: 0;
  color: #0b3150;
  font-size: 1.6rem;
}

.legal-document-header p {
  max-width: 720px;
  margin: 8px 0 0;
  color: #6d7873;
  line-height: 1.55;
}

.legal-public-link {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin-top: 10px;
  color: #0b5f8f;
  font-size: 0.86rem;
  font-weight: 700;
  text-decoration: none;
}

.legal-public-link:hover {
  text-decoration: underline;
}

.legal-public-link .material-icons-outlined {
  font-size: 1rem;
}

.edit-mode-button,
.secondary-button,
.draft-button,
.publish-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 11px 17px;
  border-radius: 9px;
  font: inherit;
  font-weight: 700;
  cursor: pointer;
}

.edit-mode-button {
  border: 1px solid #cfdbd5;
  background: #f5f9f6;
  color: #13794e;
}

.edit-mode-button .material-icons-outlined {
  font-size: 18px;
}

.legal-document-metadata {
  display: grid;
  flex-shrink: 0;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  border-top: 1px solid #e8ecea;
  border-bottom: 1px solid #e8ecea;
  background: #f8faf9;
}

.legal-document-metadata div {
  padding: 17px 22px;
  border-right: 1px solid #e1e7e4;
}

.legal-document-metadata div:last-child {
  border-right: 0;
}

.legal-document-metadata span,
.legal-document-metadata strong {
  display: block;
}

.legal-document-metadata span {
  margin-bottom: 5px;
  color: #818b86;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.legal-document-metadata strong {
  overflow: hidden;
  color: #24312b;
  font-size: 0.88rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.saved-draft-notice {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  gap: 12px;
  margin: 20px 30px 0;
  padding: 13px 15px;
  border: 1px solid #ead58b;
  border-radius: 10px;
  background: #fff9e7;
  color: #624d08;
}

.saved-draft-notice .material-icons-outlined {
  color: #a97900;
}

.saved-draft-notice strong,
.saved-draft-notice span {
  display: block;
}

.saved-draft-notice span {
  margin-top: 2px;
  font-size: 0.82rem;
}

.legal-message {
  flex-shrink: 0;
  margin: 20px 30px 0;
  padding: 12px 14px;
  border-radius: 9px;
  font-weight: 600;
}

.legal-message.error {
  background: #fff0f0;
  color: #9b3434;
}

.legal-message.success {
  background: #eaf8f0;
  color: #147448;
}

.legal-document-preview,
.legal-editor {
  padding: 30px;
}

.legal-document-preview {
  max-width: 920px;
  color: #183047;
  line-height: 1.75;
}

.legal-document-preview h1,
.legal-document-preview h2,
.legal-document-preview h3 {
  color: #0b3150;
}

.legal-document-preview h1 {
  margin: 0 0 24px;
  font-size: 2rem;
}

.legal-document-preview h2 {
  margin: 32px 0 12px;
  font-size: 1.25rem;
}

.legal-document-preview h3 {
  margin: 24px 0 10px;
  font-size: 1.05rem;
}

.legal-document-preview p,
.legal-document-preview ul,
.legal-document-preview ol {
  margin: 0 0 16px;
}

.legal-document-preview :deep(.inline-markdown-link) {
  color: #0b69a3;
  font-weight: 700;
  text-decoration: underline;
  text-underline-offset: 3px;
}

.legal-editor-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 20px;
}

.legal-editor-heading h4 {
  margin: 0;
  color: #0b3150;
  font-size: 1.2rem;
}

.legal-editor-heading p {
  margin: 6px 0 0;
  color: #75807a;
}

.draft-badge {
  padding: 7px 10px;
  border-radius: 999px;
  background: #fff4cf;
  color: #755600;
  font-size: 0.76rem;
  font-weight: 800;
  white-space: nowrap;
}

.legal-editor label {
  display: block;
  margin-bottom: 8px;
  color: #293830;
  font-weight: 700;
}

.legal-editor textarea {
  width: 100%;
  min-height: 470px;
  padding: 18px;
  resize: vertical;
  border: 1px solid #cfd8d3;
  border-radius: 11px;
  outline: none;
  background: #fbfcfb;
  color: #173047;
  font: 0.96rem/1.65 ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  box-sizing: border-box;
}

.legal-editor textarea:focus {
  border-color: #24a86c;
  box-shadow: 0 0 0 3px rgba(36, 168, 108, 0.12);
}

.legal-editor-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 18px;
}

.secondary-button {
  border: 1px solid #ccd6d1;
  background: #ffffff;
  color: #37443e;
}

.draft-button {
  border: 1px solid #d4b449;
  background: #fff8df;
  color: #755600;
}

.publish-button {
  border: 0;
  background: #18a866;
  color: #ffffff;
}

.secondary-button:disabled,
.draft-button:disabled,
.publish-button:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

@media (max-width: 900px) {
  .legal-document-metadata {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .legal-document-metadata div:nth-child(2) {
    border-right: 0;
  }

  .legal-document-metadata div:nth-child(-n + 2) {
    border-bottom: 1px solid #e1e7e4;
  }
}

@media (max-width: 620px) {
  .markdown-guide summary,
  .markdown-guide-item {
    align-items: flex-start;
  }

  .markdown-guide-grid {
    grid-template-columns: 1fr;
  }

  .markdown-guide-item {
    flex-direction: column;
    gap: 6px;
  }

  .legal-document-header,
  .legal-editor-heading {
    flex-direction: column;
  }

  .edit-mode-button {
    width: 100%;
  }

  .legal-document-metadata {
    grid-template-columns: 1fr;
  }

  .legal-document-metadata div,
  .legal-document-metadata div:nth-child(2) {
    border-right: 0;
    border-bottom: 1px solid #e1e7e4;
  }

  .legal-document-metadata div:last-child {
    border-bottom: 0;
  }

  .legal-document-header,
  .legal-document-preview,
  .legal-editor {
    padding-right: 20px;
    padding-left: 20px;
  }
}
</style>
